import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useApp } from '../../contexts/AppContext';

/**
 * 🎨 Canvas Simplificado de Pintura - VERSÃO COMPLETA
 * 
 * Modo dedicado de pintura com TODAS as funcionalidades do modal:
 * - Todos os controles inline (sem tabs)
 * - Blend modes completos
 * - Transparência para pincel e borracha
 * - Sistema de coordenadas SIMPLES
 * - Eventos ISOLADOS
 */
const SimplePaintingCanvas = ({ isActive }) => {
  const canvasContainerRef = useRef(null);
  const croquisRef = useRef(null);
  const brushRef = useRef(null);
  const brushPreviewRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [strokeCount, setStrokeCount] = useState(0);
  const [brushPreviewPosition, setBrushPreviewPosition] = useState({ x: -1000, y: -1000 });
  
  const { 
    selectedImageId, 
    uploadedImages,
    updateImage,
    paintingSettings,
  } = useApp();
  
  const selectedImage = uploadedImages.find(img => img.id === selectedImageId);
  const selectedImageIndex = uploadedImages.findIndex(img => img.id === selectedImageId);
  
  // 🎨 Usar settings do AppContext (vem da sidebar)
  const currentTool = paintingSettings.currentTool || 'pen';
  const brushSize = paintingSettings.brushSize || 20;
  const brushColor = paintingSettings.brushColor || '#000000';
  const brushOpacity = paintingSettings.brushOpacity || 100;
  const brushFlow = paintingSettings.brushFlow || 6;
  const brushSpacing = paintingSettings.brushSpacing || 2;
  const brushAngle = paintingSettings.brushAngle || 0;
  const brushRotate = paintingSettings.brushRotate || false;
  const stabilizerLevel = paintingSettings.stabilizerLevel || 10;
  const stabilizerWeight = paintingSettings.stabilizerWeight || 50;
  const currentBrushImage = paintingSettings.currentBrushImage || null;
  const blendMode = paintingSettings.blendMode || 'source-over';

  console.log('🎨 [SimplePaintingCanvas] Settings recebidos:', { 
    currentTool, 
    brushSize, 
    brushColor, 
    paintingSettings 
  });

  // ✅ Inicializar Croquis
  useEffect(() => {
    console.log('🎨 [SimplePaintingCanvas] Inicializando Croquis:', { 
      isActive, 
      selectedImage: !!selectedImage, 
      croquis: !!window.Croquis, 
      container: !!canvasContainerRef.current 
    });
    
    if (!isActive || !selectedImage || !window.Croquis || !canvasContainerRef.current) {
      return;
    }


    // Limpar canvas anterior
    if (croquisRef.current) {
      const element = croquisRef.current.getDOMElement();
      if (element?.parentNode) {
        element.parentNode.removeChild(element);
      }
      croquisRef.current = null;
    }

    // Criar Croquis
    const croquis = new window.Croquis();
    croquisRef.current = croquis;

    // Criar e configurar pincel
    const brush = new window.Croquis.Brush();
    brush.setSize(brushSize);
    brush.setColor(brushColor);
    brush.setSpacing(brushSpacing / 100);
    brush.setFlow(brushFlow / 100);
    brush.setAngle(brushAngle);
    brush.setRotateToDirection(brushRotate);
    brushRef.current = brush;

    console.log('🎨 [SimplePaintingCanvas] Configurando pincel:', { 
      currentTool, 
      brushSize, 
      brushColor, 
      brushOpacity 
    });

    croquis.setTool(brush);
    croquis.setToolStabilizeLevel(stabilizerLevel);
    croquis.setToolStabilizeWeight(stabilizerWeight / 100);
    croquis.setPaintingOpacity(brushOpacity / 100);
    croquis.setPaintingKnockout(currentTool === 'eraser');

    // Configurar canvas
    croquis.lockHistory();
    croquis.setCanvasSize(selectedImage.image.width, selectedImage.image.height);

    // 🔧 ORDEM NORMAL: FUNDO → TRABALHO → FRENTE
    // Testando ordem padrão do Croquis (última adicionada = topo)

    // 🎨 CAMADA DE FUNDO (adicionada primeiro = EMBAIXO)
    croquis.addLayer(); // Camada 0
    const backgroundCanvas = croquis.getLayers()[0].children[0];
    const bgCtx = backgroundCanvas.getContext('2d');
    bgCtx.clearRect(0, 0, selectedImage.image.width, selectedImage.image.height);

    // Removido: baseLayer não existe mais no MVP
    // A imagem de fundo será a própria imagem

    // Desenhar camadas ABAIXO (índices MENORES que selectedImageIndex)
    console.log('🎨 [SimplePaintingCanvas] Desenhando camadas ABAIXO na ordem:');
    for (let i = 0; i < selectedImageIndex; i++) {
      const layer = uploadedImages[i];
      if (layer.isVisible !== false && layer.image) {
        console.log(`  [${i}] Desenhando NO FUNDO:`, layer.name || layer.id.toString().substring(0, 15));
        bgCtx.globalAlpha = (layer.opacity || 100) / 100;
        bgCtx.globalCompositeOperation = layer.blendMode || 'source-over';
        bgCtx.drawImage(layer.image, 0, 0);
      }
    }
    bgCtx.globalAlpha = 1;
    bgCtx.globalCompositeOperation = 'source-over';

    // 🎨 CAMADA DE TRABALHO (adicionada segunda = MEIO)
    croquis.addLayer(); // Camada 1
    const workingCanvas = croquis.getLayers()[1].children[0];
    const ctx = workingCanvas.getContext('2d');
    ctx.clearRect(0, 0, selectedImage.image.width, selectedImage.image.height);
    ctx.globalAlpha = (selectedImage.opacity || 100) / 100;
    ctx.globalCompositeOperation = selectedImage.blendMode || 'source-over';
    ctx.drawImage(selectedImage.image, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    console.log('🎨 [SimplePaintingCanvas] Camada de TRABALHO:', {
      index: selectedImageIndex,
      name: selectedImage.name || selectedImage.id.toString().substring(0, 15)
    });

    // 🎨 CAMADA DE FRENTE (adicionada por último = EM CIMA)
    croquis.addLayer(); // Camada 2
    const frontCanvas = croquis.getLayers()[2].children[0];
    const frontCtx = frontCanvas.getContext('2d');
    frontCtx.clearRect(0, 0, selectedImage.image.width, selectedImage.image.height);

    // Desenhar camadas ACIMA (índices MAIORES que selectedImageIndex)
    console.log('🎨 [SimplePaintingCanvas] Desenhando camadas ACIMA na ordem:');
    for (let i = selectedImageIndex + 1; i < uploadedImages.length; i++) {
      const layer = uploadedImages[i];
      if (layer.isVisible !== false && layer.image) {
        console.log(`  [${i}] Desenhando NA FRENTE:`, layer.name || layer.id.toString().substring(0, 15));
        frontCtx.globalAlpha = (layer.opacity || 100) / 100;
        frontCtx.globalCompositeOperation = layer.blendMode || 'source-over';
        frontCtx.drawImage(layer.image, 0, 0);
      }
    }
    frontCtx.globalAlpha = 1;
    frontCtx.globalCompositeOperation = 'source-over';

    croquis.selectLayer(1); // Selecionar camada de trabalho (meio) para pintar
    croquis.setUndoLimit(100);
    croquis.unlockHistory();

    // 📊 LOG FINAL: Estrutura completa das camadas
    console.log('📊 [SimplePaintingCanvas] ESTRUTURA ORDEM NORMAL:');
    console.log('  ┌─────────────────────────────────────────┐');
    console.log('  │ Camada 2 Croquis = FRENTE (topo)       │');
    console.log('  │ Contém:', uploadedImages.slice(selectedImageIndex + 1).map((l, idx) =>
      `[${selectedImageIndex + 1 + idx}] ${l.name || l.id.toString().substring(0, 10)}`
    ).join(', ') || 'Nenhuma');
    console.log('  ├─────────────────────────────────────────┤');
    console.log('  │ Camada 1 Croquis = TRABALHO (pinta)    │');
    console.log('  │ Contém: [' + selectedImageIndex + '] ' + (selectedImage.name || selectedImage.id.toString().substring(0, 10)));
    console.log('  ├─────────────────────────────────────────┤');
    console.log('  │ Camada 0 Croquis = FUNDO (embaixo)     │');
    console.log('  │ Contém:', uploadedImages.slice(0, selectedImageIndex).map((l, idx) =>
      `[${idx}] ${l.name || l.id.toString().substring(0, 10)}`
    ).join(', ') || 'Base layer');
    console.log('  └─────────────────────────────────────────┘');
    console.log('✅ Ordem NORMAL no Croquis: Camada 0 (fundo) → Camada 1 (meio) → Camada 2 (frente)');

    // Adicionar ao DOM
    const croquisElement = croquis.getDOMElement();
    croquisElement.style.position = 'relative';
    croquisElement.style.margin = 'auto';
    croquisElement.style.maxWidth = '100%';
    croquisElement.style.maxHeight = '100%';
    croquisElement.style.display = 'block';

    const canvas = croquisElement.querySelector('canvas');
    if (canvas) {
      canvas.style.cursor = 'none';
    }

    canvasContainerRef.current.appendChild(croquisElement);

    // 🔧 NÃO MANIPULAR Z-INDEX - Deixar o Croquis gerenciar naturalmente
    // O Croquis já empilha as camadas na ordem que foram adicionadas
    console.log('✅ Canvas adicionado ao DOM. Croquis gerencia empilhamento automaticamente.');

    // Event handlers
    let isDrawing = false;

    const getRelativePosition = (e) => {
      const canvas = croquisElement.querySelector('canvas');
      if (!canvas) return null;
      
      const rect = canvas.getBoundingClientRect();
      const rx = e.clientX - rect.left;
      const ry = e.clientY - rect.top;
      
      // Verificar se está dentro dos limites do canvas
      if (rx < 0 || ry < 0 || rx > rect.width || ry > rect.height) {
        return null;
      }
      
      const scaleX = selectedImage.image.width / rect.width;
      const scaleY = selectedImage.image.height / rect.height;
      
      return {
        x: rx * scaleX,
        y: ry * scaleY,
        clientX: e.clientX,
        clientY: e.clientY
      };
    };

    const handlePointerDown = (e) => {
      console.log('🎨 [SimplePaintingCanvas] Pointer down:', e);
      e.preventDefault();
      e.stopPropagation();
      const pos = getRelativePosition(e);
      if (!pos) return;
      isDrawing = true;
      console.log('🎨 [SimplePaintingCanvas] Iniciando desenho:', { pos, currentTool });
      try {
        croquis.down(pos.x, pos.y, e.pressure || 1);
      } catch (error) {
        console.error('Erro ao iniciar traço:', error);
      }
    };

    const handlePointerMove = (e) => {
      const pos = getRelativePosition(e);
      if (!pos) {
        // Mouse fora do canvas - esconder preview
        setBrushPreviewPosition({ x: -1000, y: -1000 });
        if (!isDrawing) return;
      } else {
        // Mouse dentro do canvas - mostrar preview
        console.log('🎨 [SimplePaintingCanvas] Mouse move:', { pos, brushSize, currentTool, isDrawing });
        setBrushPreviewPosition({ x: pos.clientX, y: pos.clientY });
      }
      
      if (!isDrawing) return;
      e.preventDefault();
      e.stopPropagation();
      
      if (pos) {
        console.log('🎨 [SimplePaintingCanvas] Desenhando:', { pos, currentTool });
        try {
          croquis.move(pos.x, pos.y, e.pressure || 1);
        } catch (error) {
          console.error('Erro ao mover:', error);
        }
      }
    };

    const handlePointerUp = (e) => {
      if (!isDrawing) return;
      console.log('🎨 [SimplePaintingCanvas] Pointer up:', e);
      e.preventDefault();
      e.stopPropagation();
      const pos = getRelativePosition(e);
      isDrawing = false;
      if (pos) {
        console.log('🎨 [SimplePaintingCanvas] Finalizando desenho:', { pos, currentTool });
        try {
          croquis.up(pos.x, pos.y, e.pressure || 1);
          setStrokeCount(prev => prev + 1);
        } catch (error) {
          console.error('Erro ao finalizar traço:', error);
        }
      }
    };

    const handleCanvasMouseEnter = () => {
      // Resetar preview quando entra no canvas
    };

    const handleCanvasMouseLeave = () => {
      // Esconder preview quando sai do canvas
      setBrushPreviewPosition({ x: -1000, y: -1000 });
    };

    console.log('🎨 [SimplePaintingCanvas] Adicionando event listeners');
    croquisElement.addEventListener('pointerdown', handlePointerDown);
    croquisElement.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    croquisElement.addEventListener('mouseenter', handleCanvasMouseEnter);
    croquisElement.addEventListener('mouseleave', handleCanvasMouseLeave);

    // Aplicar blend mode inicial
    if (currentTool === 'pen') {
      croquis.setPaintingBlendMode(blendMode);
    }

    setIsInitialized(true);

    return () => {
      croquisElement.removeEventListener('pointerdown', handlePointerDown);
      croquisElement.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      croquisElement.removeEventListener('mouseenter', handleCanvasMouseEnter);
      croquisElement.removeEventListener('mouseleave', handleCanvasMouseLeave);
      setBrushPreviewPosition({ x: -1000, y: -1000 });
    };
  }, [isActive, selectedImage?.id, selectedImage?.image]);

  // Atualizar camadas de contexto quando a ordem das camadas mudar
  useEffect(() => {
    if (!croquisRef.current || !isInitialized || !selectedImage) return;
    
    const layers = croquisRef.current.getLayers();
    if (layers.length < 3) return; // Precisa ter 3 camadas
    
    console.log('🖌️ [SimplePaintingCanvas] Atualizando camadas de contexto');
    
    // Atualizar camada de fundo (Layer 0)
    const backgroundCanvas = layers[0].children[0];
    const bgCtx = backgroundCanvas.getContext('2d');
    bgCtx.clearRect(0, 0, selectedImage.image.width, selectedImage.image.height);
    
    // Removido: baseLayer não existe mais no MVP
    // A imagem de fundo será a própria imagem
    
    // Desenhar todas as camadas ABAIXO da selecionada
    for (let i = uploadedImages.length - 1; i > selectedImageIndex; i--) {
      const layer = uploadedImages[i];
      if (layer.isVisible !== false && layer.image) {
        bgCtx.globalAlpha = (layer.opacity || 100) / 100;
        bgCtx.globalCompositeOperation = layer.blendMode || 'source-over';
        bgCtx.drawImage(layer.image, 0, 0);
      }
    }
    bgCtx.globalAlpha = 1;
    bgCtx.globalCompositeOperation = 'source-over';
    
    // Atualizar camada de frente (Layer 2)
    const frontCanvas = layers[2].children[0];
    const frontCtx = frontCanvas.getContext('2d');
    frontCtx.clearRect(0, 0, selectedImage.image.width, selectedImage.image.height);
    
    // Desenhar todas as camadas ACIMA da selecionada
    for (let i = selectedImageIndex - 1; i >= 0; i--) {
      const layer = uploadedImages[i];
      if (layer.isVisible !== false && layer.image) {
        frontCtx.globalAlpha = (layer.opacity || 100) / 100;
        frontCtx.globalCompositeOperation = layer.blendMode || 'source-over';
        frontCtx.drawImage(layer.image, 0, 0);
      }
    }
    frontCtx.globalAlpha = 1;
    frontCtx.globalCompositeOperation = 'source-over';
    
  }, [uploadedImages.length, selectedImageIndex, isInitialized, selectedImage?.id]);

  // Alternar ferramenta
  useEffect(() => {
    if (!croquisRef.current || !isInitialized) return;
    croquisRef.current.setPaintingKnockout(currentTool === 'eraser');
  }, [currentTool, isInitialized]);

  // Atualizar propriedades do pincel
  useEffect(() => {
    if (!brushRef.current || !croquisRef.current || !isInitialized) return;
    console.log('🎨 [SimplePaintingCanvas] Atualizando pincel:', { brushSize, brushColor, currentTool });
    brushRef.current.setSize(brushSize);
    brushRef.current.setColor(brushColor);
    brushRef.current.setSpacing(brushSpacing / 100);
    brushRef.current.setFlow(brushFlow / 100);
    brushRef.current.setAngle(brushAngle);
    brushRef.current.setRotateToDirection(brushRotate);
    croquisRef.current.setPaintingOpacity(brushOpacity / 100);
    croquisRef.current.setToolStabilizeLevel(stabilizerLevel);
    croquisRef.current.setToolStabilizeWeight(stabilizerWeight / 100);
  }, [brushSize, brushColor, brushOpacity, brushFlow, brushSpacing, brushAngle, brushRotate, stabilizerLevel, stabilizerWeight, isInitialized]);

  // Atualizar blend mode
  useEffect(() => {
    if (!croquisRef.current || !isInitialized || currentTool === 'eraser') return;
    croquisRef.current.setPaintingBlendMode(blendMode);
  }, [blendMode, isInitialized, currentTool]);

  // Atualizar imagem do pincel
  useEffect(() => {
    if (!brushRef.current || !currentBrushImage || !isInitialized) return;
    const img = new Image();
    img.onload = () => {
      brushRef.current.setImage(img);
    };
    img.onerror = () => {
      console.error('Erro ao carregar pincel:', currentBrushImage);
    };
    img.src = currentBrushImage;
  }, [currentBrushImage, isInitialized]);

  // Atalhos de teclado (apenas Undo/Redo, controles estão na sidebar)
  useEffect(() => {
    if (!isActive || !isInitialized) return;

    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT') return;

      // Ctrl/Cmd + Z = Undo
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        handleUndo();
        e.preventDefault();
      }

      // Ctrl/Cmd + Y = Redo
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
        handleRedo();
        e.preventDefault();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, isInitialized]);

  // Funções de ação
  const handleUndo = useCallback(() => {
    if (!croquisRef.current) return;
    try {
      croquisRef.current.undo();
      setStrokeCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      // Sem undo disponível
    }
  }, []);

  const handleRedo = useCallback(() => {
    if (!croquisRef.current) return;
    try {
      croquisRef.current.redo();
      setStrokeCount(prev => prev + 1);
    } catch (error) {
      // Sem redo disponível
    }
  }, []);

  const handleApply = useCallback(() => {
    if (!croquisRef.current || !selectedImage) return;
    
    try {
      // Pegar apenas a camada de trabalho (layer 1 = camada selecionada editada)
      const workingLayer = croquisRef.current.getLayers()[1];
      if (!workingLayer) {
        console.error('Camada de trabalho não encontrada');
        return;
      }
      
      const workingCanvas = workingLayer.children[0];
      const img = new Image();
      img.onload = () => {
        updateImage(selectedImageId, {
          ...selectedImage,
          image: img,
          src: img.src
        });
        setStrokeCount(0);
        console.log('✅ Pintura aplicada à imagem');
      };
      img.src = workingCanvas.toDataURL('image/png');
    } catch (error) {
      console.error('❌ Erro ao aplicar pintura:', error);
      alert('Erro ao aplicar pintura. Tente novamente.');
    }
  }, [selectedImage, selectedImageId, updateImage]);

  const handleClear = useCallback(() => {
    if (!croquisRef.current) return;
    const confirmed = window.confirm('Limpar a camada selecionada? Esta ação não pode ser desfeita.');
    if (!confirmed) return;
    
    // Limpar apenas a camada de trabalho e redesenhar a imagem original
    const workingLayer = croquisRef.current.getLayers()[1];
    if (workingLayer && selectedImage) {
      const workingCanvas = workingLayer.children[0];
      const ctx = workingCanvas.getContext('2d');
      ctx.clearRect(0, 0, selectedImage.image.width, selectedImage.image.height);
      ctx.globalAlpha = (selectedImage.opacity || 100) / 100;
      ctx.globalCompositeOperation = selectedImage.blendMode || 'source-over';
      ctx.drawImage(selectedImage.image, 0, 0);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
    }
    setStrokeCount(0);
  }, [selectedImage]);

  // Esconder preview quando o modo de pintura é desativado
  useEffect(() => {
    if (!isActive) {
      setBrushPreviewPosition({ x: -1000, y: -1000 });
    }
  }, [isActive]);

  // Esconder preview quando o componente é desmontado
  useEffect(() => {
    return () => {
      setBrushPreviewPosition({ x: -1000, y: -1000 });
    };
  }, []);

  if (!isActive || !selectedImage) {
    console.log('🎨 [SimplePaintingCanvas] Não renderizando:', { isActive, selectedImage: !!selectedImage });
    return null;
  }

  console.log('🎨 [SimplePaintingCanvas] Renderizando canvas:', { isActive, selectedImage: selectedImage.name || selectedImage.id });

  return (
    <div className="h-full w-full flex bg-gray-900">
      {/* Preview do Pincel - só aparece quando está dentro do canvas E modo ativo */}
      {isActive && (
      <div
        ref={brushPreviewRef}
          className="fixed pointer-events-none transition-opacity duration-100"
        style={{
          left: brushPreviewPosition.x,
          top: brushPreviewPosition.y,
          transform: 'translate(-50%, -50%)',
            opacity: brushPreviewPosition.x > 0 && brushPreviewPosition.x < 9999 ? 1 : 0,
            display: brushPreviewPosition.x > 0 && brushPreviewPosition.x < 9999 ? 'block' : 'none',
            zIndex: 9999,
        }}
      >
        <div
          className="rounded-full border-2 transition-all"
          style={{
            width: Math.min(brushSize, 100),
            height: Math.min(brushSize, 100),
            borderColor: currentTool === 'pen' ? brushColor : '#ef4444',
            backgroundColor: currentTool === 'pen' ? `${brushColor}20` : '#ef444420',
          }}
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-px h-3 bg-white opacity-50"></div>
            <div className="w-3 h-px bg-white opacity-50 absolute"></div>
          </div>
              </div>
            </div>
          )}

      {/* Área do Canvas - Fullscreen */}
      <div className="flex-1 flex flex-col">
        {/* Barra de Ações Superior - Compacta */}
        <div className="bg-gray-800 border-b border-gray-700 px-3 py-2 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <div className={`p-1.5 rounded ${currentTool === 'pen' ? 'bg-green-600' : 'bg-red-600'}`}>
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {currentTool === 'pen' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                )}
              </svg>
            </div>
            <div>
              <h3 className="text-white text-sm font-semibold">
                {currentTool === 'pen' ? 'Pintura' : 'Borracha'}
              </h3>
              <p className="text-[10px] text-gray-400 leading-tight">
                {selectedImage.name || 'Imagem selecionada'}
              </p>
            </div>
          </div>

          {/* Ações */}
          <div className="flex items-center space-x-1.5">
            <button
              onClick={handleUndo}
              disabled={strokeCount === 0}
              className={`p-1.5 rounded transition-colors ${
                strokeCount > 0
                  ? 'bg-gray-700 text-white hover:bg-gray-600' 
                  : 'bg-gray-800 text-gray-600 cursor-not-allowed opacity-50'
              }`}
              title="Desfazer (Ctrl+Z)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
              </svg>
            </button>
            
            <button
              onClick={handleRedo}
              className="p-1.5 bg-gray-700 text-white hover:bg-gray-600 rounded transition-colors"
              title="Refazer (Ctrl+Y)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2m18-10l-6 6m6-6l-6-6" />
              </svg>
            </button>
            
            <button
              onClick={handleClear}
              className="p-1.5 bg-red-600 text-white hover:bg-red-700 rounded transition-colors"
              title="Limpar Camada"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>

            <div className="w-px h-5 bg-gray-600 mx-1"></div>

            <button
              onClick={handleApply}
              disabled={strokeCount === 0}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors flex items-center space-x-1.5 ${
                strokeCount > 0
                  ? 'bg-green-600 hover:bg-green-700 text-white' 
                  : 'bg-gray-800 text-gray-600 cursor-not-allowed opacity-50'
              }`}
              title={strokeCount === 0 ? "Sem alterações para aplicar" : "Aplicar pintura"}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>Aplicar</span>
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div 
          ref={canvasContainerRef}
          className="flex-1 flex items-center justify-center p-8 overflow-auto"
          style={{
            background: `
              linear-gradient(45deg, #FFFFFF 25%, transparent 25%),
              linear-gradient(-45deg, #FFFFFF 25%, transparent 25%),
              linear-gradient(45deg, transparent 75%, #FFFFFF 75%),
              linear-gradient(-45deg, transparent 75%, #FFFFFF 75%)
            `,
            backgroundColor: '#CCCCCC',
            backgroundSize: '20px 20px',
            backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
          }}
        />
      </div>
    </div>
  );
};

export default SimplePaintingCanvas;
